import { PortalModule } from "@angular/cdk/portal";
import { DOCUMENT } from "@angular/common";
import { HttpClientModule } from "@angular/common/http";
import { APP_INITIALIZER, Inject, NgModule } from "@angular/core";
import { MatTooltipModule } from "@angular/material/tooltip";
import { BrowserModule, Title } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import {
    ADMINISTRATION_ENDPOINTS,
    AdministrationModule,
    DashboardModule,
    DssAdminLibModule,
    UserProfileModule,
} from "@dtm-frontend/dss-admin-lib";
import { FlightZoneConsultationModule } from "@dtm-frontend/dss-admin-lib/flight-zone-consultation";
import { FlightZoneManagementModule } from "@dtm-frontend/dss-admin-lib/flight-zone-management";
import {
    BaseNotificationEventsService,
    FLIGHT_ZONE_ENDPOINTS,
    FlightZoneSharedModule,
    USER_PROFILE_ENDPOINTS,
} from "@dtm-frontend/dss-shared-lib";
import { AuthState, KEYCLOAK_CONFIG, SharedAuthModule } from "@dtm-frontend/shared/auth";
import { AZURE_MAPS_SUBSCRIPTION_KEY, SHARED_MAP_ENDPOINTS } from "@dtm-frontend/shared/map";
import { CesiumMapModule } from "@dtm-frontend/shared/map/cesium";
import { NOTIFICATIONS_ENDPOINTS, SharedNotificationsModule } from "@dtm-frontend/shared/notifications";
import { SharedUiModule, Theme, UI_THEME_CONFIGURATION } from "@dtm-frontend/shared/ui";
import {
    I18nRootModule,
    LANGUAGE_CONFIGURATION,
    LanguageCode,
    TRANSLATION_ENDPOINTS,
    TranslationHelperService,
    getTranslocoInlineLoader,
} from "@dtm-frontend/shared/ui/i18n";
import { VERSION_DATA_ENDPOINTS, VersionModule } from "@dtm-frontend/shared/ui/version";
import { FunctionUtils, Logger, LoggerModule, RxjsUtils } from "@dtm-frontend/shared/utils";
import { SharedWebsocketModule, SharedWebsocketModuleConfig, WEBSOCKET_CONFIGURATION } from "@dtm-frontend/shared/websocket";
import { TRANSLOCO_SCOPE } from "@jsverse/transloco";
import { LetModule, PushModule } from "@ngrx/component";
import { NgxsReduxDevtoolsPluginModule } from "@ngxs/devtools-plugin";
import { NgxsStoragePluginModule, StorageOption } from "@ngxs/storage-plugin";
import { NgxsModule, Store } from "@ngxs/store";
import { KeycloakEventType, KeycloakService } from "keycloak-angular";
import { forkJoin, lastValueFrom } from "rxjs";
import { filter, first, tap } from "rxjs/operators";
import { DssAdminEnvironment } from "../environments/environment.model";
import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { HeaderComponent } from "./components/header/header.component";
import { LazyRouterOutletComponent } from "./components/lazy-router-outlet/lazy-router-outlet.component";
import { MenuComponent } from "./components/menu/menu.component";
import { ActionsHandler } from "./services/actions.handler";
import { AnspNotificationEventsService } from "./services/ansp-notification-events.service";

Logger.initialize("https://1779efa24b445b5c36084e6f5eadbda2@sentry.pansa.cloud/5");

export function createAppModule(environment: DssAdminEnvironment) {
    @NgModule({
        declarations: [AppComponent, LazyRouterOutletComponent, HeaderComponent, MenuComponent],
        imports: [
            NgxsModule.forRoot([], {
                // NOTE: must be first because of https://github.com/ngxs/store/issues/375
                developmentMode: !environment.production,
            }),
            AppRoutingModule,
            BrowserAnimationsModule,
            BrowserModule,
            HttpClientModule,
            DssAdminLibModule,
            NgxsStoragePluginModule.forRoot({
                key: ["ui"],
                storage: StorageOption.LocalStorage,
            }),
            NgxsReduxDevtoolsPluginModule.forRoot({
                disabled: environment.production,
                name: "DssAdmin",
            }),
            FlightZoneManagementModule.forRoot(),
            FlightZoneConsultationModule.forRoot(),
            AdministrationModule.forRoot(),
            DashboardModule,
            UserProfileModule,
            FlightZoneSharedModule,
            PortalModule,
            LetModule,
            PushModule,
            LoggerModule.forRoot(environment.name, !environment.production),
            SharedAuthModule.forRoot({
                bearerExcludedUrls: [...environment.bearerExcludedUrls],
            }),
            I18nRootModule.forRoot({
                developmentMode: !environment.production,
                ...LANGUAGE_CONFIGURATION,
            }),
            SharedUiModule,
            VersionModule.forRoot(),
            SharedWebsocketModule.forRoot(),
            SharedNotificationsModule.forRoot(),
            CesiumMapModule,
            MatTooltipModule,
        ],
        providers: [
            {
                provide: FLIGHT_ZONE_ENDPOINTS,
                useValue: environment.flightZoneEndpoints,
            },
            {
                provide: VERSION_DATA_ENDPOINTS,
                useValue: environment.versionDataEndpoints,
            },
            {
                provide: NOTIFICATIONS_ENDPOINTS,
                useValue: environment.notificationEndpoints,
            },
            {
                provide: USER_PROFILE_ENDPOINTS,
                useValue: environment.userProfileEndpoints,
            },
            {
                provide: ADMINISTRATION_ENDPOINTS,
                useValue: environment.administrationEndpoints,
            },
            {
                provide: TRANSLATION_ENDPOINTS,
                useValue: environment.translationEndpoints,
            },
            {
                provide: SHARED_MAP_ENDPOINTS,
                useValue: environment.sharedMapEndpoints,
            },
            {
                provide: AZURE_MAPS_SUBSCRIPTION_KEY,
                useValue: environment.azureMapsSubscriptionKey,
            },
            {
                provide: WEBSOCKET_CONFIGURATION,
                useFactory: (store: Store): SharedWebsocketModuleConfig => ({
                    endpoint: environment.webSocketEndpoint,
                    authorizationTokenProvider: async () =>
                        await lastValueFrom(store.select(AuthState.token).pipe(RxjsUtils.filterFalsy(), first())),
                }),
                deps: [Store],
            },
            {
                provide: KEYCLOAK_CONFIG,
                useValue: environment.keycloakConfig,
            },
            {
                provide: UI_THEME_CONFIGURATION,
                useValue: { defaultTheme: Theme.Light, supportedThemes: [Theme.Light] },
            },
            {
                provide: APP_INITIALIZER,
                useFactory: () => FunctionUtils.noop,
                deps: [ActionsHandler],
                multi: true,
            },
            {
                provide: TRANSLOCO_SCOPE,
                multi: true,
                useValue: {
                    scope: "dssAdmin",
                    loader: getTranslocoInlineLoader((language: LanguageCode) => import(`../assets/i18n/${language}.json`)),
                },
            },
            {
                provide: BaseNotificationEventsService,
                useClass: AnspNotificationEventsService,
            },
        ],
        bootstrap: [AppComponent],
    })
    class AppModule {
        constructor(
            keycloakService: KeycloakService,
            translocoHelper: TranslationHelperService,
            titleService: Title,
            @Inject(DOCUMENT) document: Document
        ) {
            // TODO: is this a good place for some initializing tasks? Maybe it should be extracted somewhere, use APP_INITIALIZER?
            const gotUserData$ = keycloakService.keycloakEvents$.pipe(
                filter((event) => event?.type === KeycloakEventType.OnReady),
                first()
            );

            const gotTitle$ = translocoHelper
                .waitForTranslation("dssAdmin.appTitle")
                .pipe(first(), tap(titleService.setTitle.bind(titleService)));

            forkJoin([gotUserData$, gotTitle$])
                .pipe(
                    first(),
                    tap(() => {
                        document.getElementById("loader")?.remove();
                    })
                )
                .subscribe();
        }
    }

    return AppModule;
}
